import { useEffect, useRef } from 'react';
import { paymentAppUrl, usesMobilePaymentHandoff } from '../payments/links';

const KEY = '__vibesplitPaymentReturn';
export function clearPaymentReturn() {
  const { [KEY]: removed, ...rest } = window.history.state || {};
  window.history.replaceState(rest, '');
}
export function paymentAppOpensInNewTab(url: string) {
  const mobile = usesMobilePaymentHandoff(navigator.userAgent, navigator.platform, navigator.maxTouchPoints);
  return /^https?:\/\//i.test(url) && !mobile;
}

// Call directly from the acknowledgement click, before any asynchronous work.
// The history snapshot also preserves the exact selection on reload/browser Back.
export function navigateToPaymentApp<T>(url: string, selection: T) {
  const previousState = window.history.state;
  window.history.replaceState({ ...window.history.state, [KEY]: {
    path: window.location.pathname, createdAt: Date.now(), selection,
  } }, '');
  try {
    if (paymentAppOpensInNewTab(url)) {
      const paymentTab = window.open(url, '_blank');
      if (!paymentTab) throw new Error('Payment tab blocked');
      // New contexts initially contain about:blank. Sever the opener before the
      // external page loads, while retaining the handle to detect a blocked tab.
      paymentTab.opener = null;
      return 'new-tab' as const;
    }
    window.location.assign(paymentAppUrl(url));
    return 'same-tab' as const;
  } catch (error) {
    window.history.replaceState(previousState, '');
    throw error;
  }
}

export default function usePaymentReturn<T>(onReturn: (selection: T) => void, ready = true) {
  const callback = useRef(onReturn);
  callback.current = onReturn;
  useEffect(() => {
    if (!ready) return;
    const restore = () => {
      if (document.hidden) return;
      const state = window.history.state;
      const pending = state?.[KEY];
      if (!pending || pending.path !== window.location.pathname) return;
      const { [KEY]: removed, ...rest } = state;
      window.history.replaceState(rest, '');
      if (Date.now() - pending.createdAt < 60 * 60 * 1000) callback.current(pending.selection);
    };
    restore();
    window.addEventListener('pageshow', restore);
    window.addEventListener('focus', restore);
    document.addEventListener('visibilitychange', restore);
    return () => {
      window.removeEventListener('pageshow', restore);
      window.removeEventListener('focus', restore);
      document.removeEventListener('visibilitychange', restore);
    };
  }, [ready]);
}
